import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { ContentBlock } from "@fobu/schema";
import { safeHref } from "@fobu/engine";
import { useFormEnv } from "../lib/context";
import { t } from "../lib/i18n";
import { Markdown, plain } from "../lib/markdown";

type Of<T extends ContentBlock["type"]> = Extract<ContentBlock, { type: T }>;


type DialogProps = { label: string; onClose: () => void; children: ComponentChildren; extra?: ComponentChildren };
let loaded: ((p: DialogProps) => preact.JSX.Element) | null = null;
/** Große Ansicht wird erst beim ersten Öffnen nachgeladen (Größenbudget des Formulars). */
export function MediaDialog(p: DialogProps) {
  const [View, setView] = useState(() => loaded);
  useEffect(() => {
    if (!View) void import("./MediaDialog").then((m) => ((loaded = m.MediaDialog), setView(() => m.MediaDialog)));
  }, []);
  return View ? <View {...p} /> : null;
}

export function ImageBlock({ block }: { block: Of<"image"> }) {
  const { tpl } = useFormEnv();
  const [zoom, setZoom] = useState(false);
  const caption = block.caption ? tpl(block.caption) : "";
  const href = block.click === "link" && block.href ? safeHref(block.href) : null;
  const img = <img src={block.url} alt={block.alt} loading="lazy" decoding="async" />;
  const label = block.alt || plain(caption) || t.mediaEnlarge;
  return (
    <figure class={`fb-figure fb-figure--${block.layout} fb-figure--${block.size}`}>
      {block.click === "zoom" ? (
        <button type="button" class="fb-figure-zoom" aria-label={`${t.mediaEnlarge}: ${label}`} aria-haspopup="dialog" onClick={() => setZoom(true)}>
          {img}
        </button>
      ) : href ? (
        <a class="fb-figure-link" href={href} target="_blank" rel="noopener noreferrer">
          {img}
        </a>
      ) : (
        img
      )}
      {caption && (
        <figcaption>
          <Markdown text={caption} />
        </figcaption>
      )}
      {zoom && (
        <MediaDialog label={label} onClose={() => setZoom(false)}>
          <img class="fb-dialog-media" src={block.url} alt={block.alt} />
          {caption && (
            <p class="fb-dialog-caption">
              <Markdown text={caption} />
            </p>
          )}
        </MediaDialog>
      )}
    </figure>
  );
}

type Extra = typeof import("./MediaExtra");
let extraMod: Extra | null = null;
/** Video und Dokument sind selten – sie werden nachgeladen (Größenbudget des Formulars). */
function useExtra() {
  const [m, setM] = useState(() => extraMod);
  useEffect(() => {
    if (!m) void import("./MediaExtra").then((x) => ((extraMod = x), setM(() => x)));
  }, []);
  return m;
}
export function VideoBlock({ block }: { block: Of<"video"> }) {
  const m = useExtra();
  return m ? <m.VideoBlock block={block} /> : <div class="fb-media-wait" />;
}
export function DocumentBlock({ block }: { block: Of<"document"> }) {
  const m = useExtra();
  return m ? <m.DocumentBlock block={block} /> : <div class="fb-media-wait" />;
}
